import { UUID } from "@bitory-gmbh/nidavellir";
import { DocumentKind, ServiceKind } from "@bitory-gmbh/bsm";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  UpdateRequest,
  useConnectDocumentDataCreateMutation,
  useConnectDocumentDataShowQuery,
  useConnectDocumentDataUpdateMutation,
} from "@/services/backend/connect/api/service";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { DocumentData } from "@/services/backend/connect/types/document-data";
import { TextInput } from "@/shared/components/form/text-input";
import { useEffect, useState } from "react";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import {
  RtkQueryMutation,
  useDebouncedMutation,
} from "@/shared/lib/debounce/debounce";
import {
  is404,
  parseRTKQueryErrorToSonner,
  rtkQueryBackendErrorIs,
} from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import { v4 } from "uuid";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import type { RootState } from "@/shared/lib/store/store";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import {
  ErrReferenceNumberConflict,
  Reference,
  ReferenceNumberPatchRequest,
} from "@/services/backend/documents/types/reference-number";
import {
  updateHtzWorkOrderCache,
  useHtzWorkOrderShowQuery,
  useHtzWorkOrderPatchMutation,
} from "@/services/backend/htz/work-order/api/work-order-api";
import {
  updateRltWorkOrderCache,
  useRltWorkOrderShowQuery,
  useRltWorkOrderPatchMutation,
} from "@/services/backend/rlt/work-orders/api/work-order-api";
import {
  updateVbsWorkOrderCache,
  useVbsWorkOrderShowQuery,
  useVbsWorkOrderPatchMutation,
} from "@/services/backend/vbs/work-orders/api/work-order-api";
import { DocumentStateBase } from "@/services/backend/documents/types/state";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";

interface ConnectDocumentDataCardProps {
  serviceKind: ServiceKind;
  documentKind: DocumentKind;
  documentId: UUID;
  disabled?: boolean;
}

/**
 * ConnectDocumentDataCard edits the connect data of a document. The data is
 * an entity of its own, so the card holds its field errors in a
 * ErrValidationFailedContextProvider of its own, apart from the document's.
 */
export function ConnectDocumentDataCard(props: ConnectDocumentDataCardProps) {
  return (
    <ErrValidationFailedContextProvider>
      <ConnectDocumentDataCardInContext {...props} />
    </ErrValidationFailedContextProvider>
  );
}

function ConnectDocumentDataCardInContext({
  serviceKind,
  documentKind,
  documentId,
  disabled,
}: ConnectDocumentDataCardProps) {
  const {
    data: documentData,
    isLoading,
    error,
    refetch,
  } = useConnectDocumentDataShowQuery({
    serviceKind,
    documentKind,
    documentId,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Connect Daten")}</CardTitle>
      </CardHeader>
      <CardContent>
        {is404(error) && (
          <CreateConnectDataButton
            serviceKind={serviceKind}
            documentKind={documentKind}
            documentId={documentId}
            disabled={disabled}
            onSuccess={refetch}
          />
        )}
        {error && !is404(error) && <RTKQueryErrorAlert error={error} />}
        {isLoading && <CardContentSkeleton />}
        {documentData && (
          <DocumentDataFields
            documentData={documentData}
            serviceKind={serviceKind}
            documentKind={documentKind}
            documentId={documentId}
            disabled={disabled}
          />
        )}
      </CardContent>
    </Card>
  );
}

function DocumentDataFields({
  documentData,
  serviceKind,
  documentKind,
  documentId,
  disabled,
}: {
  documentData: DocumentData;
  serviceKind: ServiceKind;
  documentKind: DocumentKind;
  documentId: UUID;
  disabled?: boolean;
}) {
  const [request, setRequest] = useState<UpdateRequest>({
    id: documentData.id,
    number: documentData.number,
    customerNumber: documentData.customerNumber,
    objectNumber: documentData.objectNumber,
  });
  const mutation = useConnectDocumentDataUpdateMutation();
  const { error } = useDebouncedMutation(request, setRequest, mutation, {
    id: documentData.id,
    number: documentData.number,
    customerNumber: documentData.customerNumber,
    objectNumber: documentData.objectNumber,
  });

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="grid gap-1">
        <TextInput
          label={t("Nummer")}
          field="number"
          text={request.number}
          onTextChange={(number) => setRequest({ ...request, number })}
          errors={inputValidationErrorMessages(error, "number")}
          disabled={disabled}
        />
        {documentKind === GSMDocumentKind.WorkOrder && (
          <ApplyConnectNumberButton
            serviceKind={serviceKind}
            workOrderId={documentId}
            connectNumber={request.number}
          />
        )}
      </div>
      <TextInput
        label={t("Kundennummer")}
        field="customerNumber"
        text={request.customerNumber}
        onTextChange={(customerNumber) =>
          setRequest({ ...request, customerNumber })
        }
        errors={inputValidationErrorMessages(error, "customerNumber")}
        disabled={disabled}
      />
      <TextInput
        label={t("Objektnummer")}
        field="objectNumber"
        text={request.objectNumber}
        onTextChange={(objectNumber) =>
          setRequest({ ...request, objectNumber })
        }
        errors={inputValidationErrorMessages(error, "objectNumber")}
        disabled={disabled}
      />
    </div>
  );
}

function CreateConnectDataButton({
  serviceKind,
  documentKind,
  documentId,
  disabled,
  onSuccess,
}: {
  serviceKind: ServiceKind;
  documentKind: DocumentKind;
  documentId: UUID;
  disabled?: boolean;
  onSuccess?: () => void;
}) {
  const [post, { isLoading, isSuccess, error }] =
    useConnectDocumentDataCreateMutation();

  const doCreate = () => {
    if (isLoading) return;

    post({ id: v4(), serviceKind, documentKind, documentId });
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success("Connect Daten Erfassung aktiviert.");
      onSuccess?.();
    }
  }, [isSuccess, onSuccess]);

  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
    }
  }, [error]);

  return (
    <Button onClick={doCreate} disabled={isLoading || disabled}>
      {isLoading && <RefreshCw className="mr-2 animate-spin" />}
      {t("Connect Daten erfassen")}
    </Button>
  );
}

interface ApplyConnectNumberProps {
  workOrderId: UUID;
  connectNumber: string;
}

/**
 * ApplyConnectNumberButton appends the connect number to the reference
 * number of the work order.
 */
function ApplyConnectNumberButton({
  serviceKind,
  ...props
}: ApplyConnectNumberProps & { serviceKind: ServiceKind }) {
  switch (serviceKind) {
    case GSMServiceKind.HTZ:
      return <HtzApplyConnectNumberButton {...props} />;
    case GSMServiceKind.RLT:
      return <RltApplyConnectNumberButton {...props} />;
    case GSMServiceKind.VBS:
      return <VbsApplyConnectNumberButton {...props} />;
    default:
      return null;
  }
}

function HtzApplyConnectNumberButton({
  workOrderId,
  connectNumber,
}: ApplyConnectNumberProps) {
  const { data } = useHtzWorkOrderShowQuery({ id: workOrderId });
  const mutation = useHtzWorkOrderPatchMutation();
  return (
    <ApplyAsReferenceNumberButton
      workOrder={data}
      connectNumber={connectNumber}
      mutation={mutation}
      updateCache={updateHtzWorkOrderCache}
    />
  );
}

function RltApplyConnectNumberButton({
  workOrderId,
  connectNumber,
}: ApplyConnectNumberProps) {
  const { data } = useRltWorkOrderShowQuery({ id: workOrderId });
  const mutation = useRltWorkOrderPatchMutation();
  return (
    <ApplyAsReferenceNumberButton
      workOrder={data}
      connectNumber={connectNumber}
      mutation={mutation}
      updateCache={updateRltWorkOrderCache}
    />
  );
}

function VbsApplyConnectNumberButton({
  workOrderId,
  connectNumber,
}: ApplyConnectNumberProps) {
  const { data } = useVbsWorkOrderShowQuery({ id: workOrderId });
  const mutation = useVbsWorkOrderPatchMutation();
  return (
    <ApplyAsReferenceNumberButton
      workOrder={data}
      connectNumber={connectNumber}
      mutation={mutation}
      updateCache={updateVbsWorkOrderCache}
    />
  );
}

interface ReferencedWorkOrder extends Reference, DocumentStateBase {
  id: string;
}

/**
 * withConnectNumber appends the connect number to the reference number of a
 * document. A connect number appended earlier is replaced, so applying it
 * again after the number changed in connect does not pile up suffixes.
 */
function withConnectNumber(referenceNumber: string, connectNumber: string) {
  const base = referenceNumber.replace(/\s*Connect:.*$/, "").trim();
  return `${base} Connect: ${connectNumber}`.trim();
}

function ApplyAsReferenceNumberButton<T extends ReferencedWorkOrder>({
  workOrder,
  connectNumber,
  mutation,
  updateCache,
}: {
  workOrder: T | undefined;
  connectNumber: string;
  mutation: RtkQueryMutation<ReferenceNumberPatchRequest, T>;
  updateCache: (
    value: T,
  ) => ThunkAction<unknown, RootState, unknown, UnknownAction>;
}) {
  const dispatch = useAppDispatch();
  const [update, { isLoading }] = mutation;

  const number = connectNumber.trim();
  const referenceNumber = workOrder
    ? withConnectNumber(workOrder.referenceNumber, number)
    : "";
  // Work orders without a reference number (e.g. of commitments) are not
  // numbered and take no connect number.
  const enabled =
    !!workOrder &&
    !isImmutable(workOrder) &&
    workOrder.referenceNumber !== "" &&
    number !== "" &&
    referenceNumber !== workOrder.referenceNumber;

  const doApply = () => {
    if (!workOrder || isLoading) return;

    update({ id: workOrder.id, referenceNumber })
      .unwrap()
      .then((updated) => {
        dispatch(updateCache(updated));
        toast.success(t("Connect-Nummer an die Nummer angehängt."));
      })
      .catch((error: unknown) => {
        if (rtkQueryBackendErrorIs(error, ErrReferenceNumberConflict)) {
          toast.error(
            t("Nummer wird bereits von einem anderen Dokument verwendet."),
          );
          return;
        }
        toast(
          ...parseRTKQueryErrorToSonner(
            error as Parameters<typeof parseRTKQueryErrorToSonner>[0],
            false,
          ),
        );
      });
  };

  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      className="justify-self-start px-0"
      onClick={doApply}
      disabled={!enabled || isLoading}
    >
      {isLoading && <RefreshCw className="animate-spin" />}
      {t("Connect-Nummer anhängen")}
    </Button>
  );
}
